import type { CSSProperties, HTMLAttributes } from 'react';

import styles from './Skeleton.module.css';

type Variant = 'text' | 'block' | 'circle';

interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: Variant;
  width?: number | string;
  height?: number | string;
  /**
   * Optional ARIA label. If omitted the skeleton is hidden from AT —
   * surrounding live regions should announce loading state.
   */
  label?: string;
}

/**
 * Brand-tinted shimmer placeholder. Renders an inline-block span by default
 * so it can sit inside flow content; pass `variant="block"` for a full-width
 * card or list-row placeholder.
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  label,
  style,
  className,
  ...rest
}: SkeletonProps) {
  const cssVars: CSSProperties = {
    width: width ?? (variant === 'circle' ? height ?? 32 : '100%'),
    height: height ?? (variant === 'text' ? '0.85em' : variant === 'circle' ? 32 : 16),
    ...style,
  };

  const ariaProps = label
    ? { role: 'status' as const, 'aria-live': 'polite' as const, 'aria-label': label }
    : { 'aria-hidden': true as const };

  const classes = [styles.skeleton, styles[`variant_${variant}`], className]
    .filter(Boolean)
    .join(' ');

  return <span {...ariaProps} {...rest} className={classes} style={cssVars} />;
}
